/**
 * FollowerState — pure-logic state machine for the lead Pokémon that walks
 * one tile behind the player (HGSS-style follower system).
 *
 * NO Phaser dependencies. The companion `follower.ts` (commit 3) owns the
 * Phaser.Sprite + tweens and observes this state to drive animations. All
 * 33 unit specs target THIS file (see test plan
 * `~/.cstack/projects/craigxyz-poke/craig-main-eng-review-test-plan-20260511-013148.md`
 * "Updated test specs" section).
 *
 * Position is **derived, not persisted**. No save schema bump. On reload,
 * WorldScene calls `spawn(...)` or `onMapLoad(...)` with the restored
 * `runState.position` + facing and FollowerState re-computes the tile.
 *
 * Lifecycle methods follow the cartridge HGSS convention:
 *  - Walking: follower targets the player's previous tile on each step.
 *  - Ledge hop: 1-step delay (player hops; follower hops on the NEXT step).
 *  - Spawn / map load / lead change: send-out animation gate
 *    (`sendOutInProgress`) the WorldScene reads to lock player movement
 *    for the ~1s of the Pokéball-pop.
 *  - NPC walks onto follower's tile: auto-shuffle (cardinal first,
 *    diagonal fallback); hide + pendingReveal if all 8 blocked.
 *  - Scene SLEEP (battle / draft / menu): snap-and-hide. WAKE re-derives.
 *  - Dialogue open: freeze (visual continuity). Close: resume observation.
 *  - flushPending: pre-dialogue / pre-encounter resolution of pending hops
 *    or in-flight send-outs so the next state transition starts clean.
 */

export type Facing = 'up' | 'down' | 'left' | 'right';

export interface Tile {
  x: number;
  y: number;
}

/** Distinguishes the three tween kinds the wrapper plays in response to a
 *  state transition. The wrapper reads `targetTile` + `tweenKind` from the
 *  snapshot and chooses the Phaser tween (140ms linear / sin-arc /
 *  Pokéball-pop). Null when no tween is in flight. */
export type TweenKind = 'step' | 'hop' | 'sendOut';

export interface FollowerSnapshot {
  tileX: number;
  tileY: number;
  /** Cardinal facing direction. Updated by spawn / onPlayerStepStart /
   *  onMapLoad / onWake / onPlayerFacingChange. The Follower wrapper
   *  reads this to pick the right frame from the 9-frame overworld
   *  spritesheet (down/up/right; left uses right + flipX). Default
   *  'down' on fresh construction. */
  facing: Facing;
  /** Set when a tween is in flight. Null when idle. The wrapper tweens
   *  the sprite from (tileX, tileY) → targetTile, then calls
   *  `onPlayerStepComplete()` to commit. */
  targetTile: Tile | null;
  /** Wall-clock ms when the current tween started — passed through from
   *  the WorldScene's tween-start time so the wrapper + tests can reason
   *  about elapsed-in-flight. */
  tweenStartMs: number | null;
  tweenKind: TweenKind | null;
  /** True after a 2-tile player hop. Resolves on the NEXT
   *  `onPlayerStepStart` (cartridge 1-step delay). */
  hopPending: boolean;
  /** True during the Pokéball-pop send-out window (~1s). WorldScene gates
   *  `tryProcessMovement` + `checkEncounter` on this so the player can't
   *  walk or step into grass mid-animation. */
  sendOutInProgress: boolean;
  /** True when spawn / onMapLoad / NPC-bump found all 8 adjacent blocked
   *  and the follower had to hide. Resolves on the next
   *  `onPlayerStepStart` (player moves away, vacating the home tile). */
  pendingReveal: boolean;
  /** Lowercase @pkmn species id (e.g. 'bulbasaur'). Null when the party
   *  is empty (post-Reset-Run, before re-entering Oak's Lab). */
  species: string | null;
  visible: boolean;
  /** True while a dialogue is open. The wrapper pauses its Phaser tween
   *  on `onDialogueOpen()`; FollowerState's internal state otherwise
   *  continues — the freeze is a visual-continuity concern, not a logical
   *  one. */
  dialogueOpen: boolean;
}

const FACING_VECTORS: Readonly<Record<Facing, { dx: number; dy: number }>> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

/** Cardinal offsets in N, E, S, W order — the auto-shuffle priority when
 *  the canonical "behind" tile is unavailable. */
const CARDINAL_OFFSETS: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: 0, dy: -1 }, // N
  { dx: 1, dy: 0 }, // E
  { dx: 0, dy: 1 }, // S
  { dx: -1, dy: 0 }, // W
];

/** Diagonal fallback when all 4 cardinals are blocked. NE, SE, SW, NW. */
const DIAGONAL_OFFSETS: ReadonlyArray<{ dx: number; dy: number }> = [
  { dx: 1, dy: -1 }, // NE
  { dx: 1, dy: 1 }, // SE
  { dx: -1, dy: 1 }, // SW
  { dx: -1, dy: -1 }, // NW
];

function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

function isBlocked(x: number, y: number, blocked: ReadonlySet<string> | undefined): boolean {
  if (!blocked) return false;
  return blocked.has(tileKey(x, y));
}

function tilesEqual(a: Tile, b: Tile): boolean {
  return a.x === b.x && a.y === b.y;
}

function freshState(): FollowerSnapshot {
  return {
    tileX: 0,
    tileY: 0,
    facing: 'down',
    targetTile: null,
    tweenStartMs: null,
    tweenKind: null,
    hopPending: false,
    sendOutInProgress: false,
    pendingReveal: false,
    species: null,
    visible: false,
    dialogueOpen: false,
  };
}

export class FollowerState {
  private state: FollowerSnapshot = freshState();

  /** Cheap accessor for the WorldScene's player-movement + grass-step
   *  encounter gate. The gate is checked on every update() tick; the
   *  full snapshot() does a deep copy, which is wasteful when only one
   *  boolean is needed. */
  get sendOutInProgress(): boolean {
    return this.state.sendOutInProgress;
  }

  /** Deep-copy snapshot for tests + wrapper observation. The wrapper
   *  shouldn't mutate FollowerState directly. */
  snapshot(): FollowerSnapshot {
    const s = this.state;
    return {
      tileX: s.tileX,
      tileY: s.tileY,
      facing: s.facing,
      targetTile: s.targetTile ? { x: s.targetTile.x, y: s.targetTile.y } : null,
      tweenStartMs: s.tweenStartMs,
      tweenKind: s.tweenKind,
      hopPending: s.hopPending,
      sendOutInProgress: s.sendOutInProgress,
      pendingReveal: s.pendingReveal,
      species: s.species,
      visible: s.visible,
      dialogueOpen: s.dialogueOpen,
    };
  }

  /**
   * Initial spawn. Places the follower one tile behind the player
   * (opposite of facing direction), auto-shuffles to another walkable
   * adjacent if the canonical tile is blocked, or hides + flags
   * pendingReveal if all 8 adjacent tiles are blocked. Triggers a
   * send-out animation on the chosen tile (or no-op if hidden).
   */
  spawn(
    playerTile: Tile,
    facing: Facing,
    species: string,
    npcTiles?: ReadonlySet<string>,
    preferredTile?: Tile,
  ): void {
    this.state.species = species;
    this.state.facing = facing;
    // Round-5 lab refinement: a caller may request a specific spawn
    // tile (the post-pick starter appears at the player's RIGHT so it
    // stands clear of the rival's row-5 table corridor and watches the
    // cinematic beside you). Falls back to behind-or-shuffle whenever
    // the preferred tile is blocked; trailing resumes normally on the
    // player's next step either way (onPlayerStepStart is placement-
    // agnostic — it always tweens to the player's vacated tile).
    this.placeBehindOrShuffle(playerTile, facing, npcTiles, preferredTile);
  }

  /**
   * Player just started a step from oldTile to newTile. Follower:
   *   - turn-in-place (oldTile == newTile): no-op
   *   - sendOutInProgress: no-op (player should be locked anyway —
   *     defensive)
   *   - pendingReveal: appear at oldTile (player vacated, so it's
   *     walkable now); no tween, no send-out
   *   - hopPending: hop with sin-arc to oldTile (player's previous /
   *     landing-after-hop tile); hopPending → false
   *   - default: straight tween to oldTile (player's previous tile)
   */
  onPlayerStepStart(
    oldTile: Tile,
    newTile: Tile,
    facing: Facing,
    tweenStartMs: number,
  ): void {
    if (tilesEqual(oldTile, newTile)) return; // turn in place
    if (this.state.sendOutInProgress) return; // defensive — player locked

    // Follower walks in the direction of the step (matches player's
    // facing on a successful step). Updated BEFORE the tween fires so
    // the Follower wrapper picks the correct directional frame.
    this.state.facing = facing;

    if (this.state.pendingReveal) {
      this.state.tileX = oldTile.x;
      this.state.tileY = oldTile.y;
      this.state.targetTile = null;
      this.state.tweenStartMs = null;
      this.state.tweenKind = null;
      this.state.visible = true;
      this.state.pendingReveal = false;
      return;
    }

    const target: Tile = { x: oldTile.x, y: oldTile.y };
    if (this.state.hopPending) {
      this.state.hopPending = false;
      this.state.targetTile = target;
      this.state.tweenStartMs = tweenStartMs;
      this.state.tweenKind = 'hop';
      return;
    }

    this.state.targetTile = target;
    this.state.tweenStartMs = tweenStartMs;
    this.state.tweenKind = 'step';
  }

  /** Wrapper signals the in-flight step / hop tween completed. Commit
   *  targetTile → tileX/tileY and clear tween fields. No-op if there's
   *  no tween in flight. */
  onPlayerStepComplete(): void {
    if (!this.state.targetTile) return;
    this.state.tileX = this.state.targetTile.x;
    this.state.tileY = this.state.targetTile.y;
    this.state.targetTile = null;
    this.state.tweenStartMs = null;
    this.state.tweenKind = null;
  }

  /** Player initiated a 2-tile ledge hop. Set hopPending; follower does
   *  NOT animate yet. The 1-step delay (cartridge HGSS convention) means
   *  the hop animation fires on the next `onPlayerStepStart`. */
  onPlayerHopStart(_oldTile: Tile, _newTile: Tile): void {
    this.state.hopPending = true;
  }

  /** New map loaded (warp, edge-cross, resume on reload). Re-derive the
   *  follower's position from the player's tile + facing and play the
   *  send-out animation. Equivalent to `spawn` semantically but uses the
   *  current `species` rather than re-setting it. */
  onMapLoad(
    playerTile: Tile,
    facing: Facing,
    npcTiles?: ReadonlySet<string>,
  ): void {
    this.state.facing = facing;
    this.placeBehindOrShuffle(playerTile, facing, npcTiles);
  }

  /** Scene tearing down (battle / draft / menu / end-run / popover with
   *  scene-sleep). Snap to home tile (targetTile if tweening, else
   *  current), clear all transient state, hide. `species` is preserved
   *  for the upcoming `onWake`. */
  onSleep(): void {
    if (this.state.targetTile) {
      this.state.tileX = this.state.targetTile.x;
      this.state.tileY = this.state.targetTile.y;
    }
    this.state.targetTile = null;
    this.state.tweenStartMs = null;
    this.state.tweenKind = null;
    this.state.hopPending = false;
    this.state.sendOutInProgress = false;
    this.state.pendingReveal = false;
    this.state.dialogueOpen = false;
    this.state.visible = false;
  }

  /** Scene resuming. Re-derive position from player tile + facing using
   *  the preserved species. Replays send-out.
   *
   *  `preferredTile` overrides the derivation when it's walkable: the
   *  in-world duel re-aims the player's facing at the opponent for the
   *  fight, so "behind the player" points somewhere new by the time
   *  the duel ends — deriving would teleport the follower across the
   *  player the instant the world came back. The duel passes the tile
   *  the follower actually slept on (which is also where the battler
   *  walks home to), so it simply steps back into its own footprints. */
  onWake(
    playerTile: Tile,
    facing: Facing,
    npcTiles?: ReadonlySet<string>,
    preferredTile?: Tile,
  ): void {
    if (!this.state.species) return; // no party — nothing to wake
    this.state.facing = facing;
    this.placeBehindOrShuffle(playerTile, facing, npcTiles, preferredTile);
  }

  /** Player turned in place without stepping (faced a wall, switched
   *  control input mid-tween, etc.). Update the follower's facing so
   *  the wrapper renders the correct directional sprite, even though
   *  no movement happens. Cartridge HGSS convention: follower turns
   *  with the player. */
  onPlayerFacingChange(facing: Facing): void {
    this.state.facing = facing;
  }

  /** Dialogue opening — set flag for the wrapper to pause its Phaser
   *  tween. FollowerState's internal state doesn't change (the freeze is
   *  visual-only); tween fields persist so dialogue close can resume. */
  onDialogueOpen(): void {
    this.state.dialogueOpen = true;
  }

  onDialogueClose(): void {
    this.state.dialogueOpen = false;
  }

  /**
   * NPC tile set changed (NPC walked onto / off of a tile). If the
   * follower's home tile is now blocked, auto-shuffle to nearest walkable
   * adjacent (cardinal first, diagonal fallback). If all 8 blocked, hide
   * + pendingReveal. No-op if follower's current tile is still walkable.
   */
  onNpcTilesChange(npcTiles: ReadonlySet<string>): void {
    if (!this.state.visible) return; // hidden — next step will resolve
    if (this.state.targetTile) return; // mid-tween; let it complete first
    const home = { x: this.state.tileX, y: this.state.tileY };
    if (!isBlocked(home.x, home.y, npcTiles)) return; // still walkable

    const shuffled = this.findAdjacentWalkable(home, npcTiles);
    if (shuffled) {
      // Targeting via tween so the wrapper can animate the 140ms slide.
      // tweenStartMs not set here — the wrapper supplies it via its
      // own time source on observation (or the next observed snapshot
      // diff). Simpler: leave tweenStartMs null; the wrapper triggers
      // the tween with its own start time.
      this.state.targetTile = shuffled;
      this.state.tweenKind = 'step';
      // No tweenStartMs — wrapper-driven for shuffle.
    } else {
      this.state.visible = false;
      this.state.pendingReveal = true;
    }
  }

  /**
   * Lead Pokémon changed (orb chrome "Set as lead" reorder). Cancel any
   * in-flight send-out, swap species, start a fresh send-out on the
   * current tile. The wrapper handles texture swap (synchronous if the
   * new species' framesheet is preloaded; static `.frame.png` fallback +
   * lazy spritesheet load otherwise).
   */
  onLeadChanged(species: string): void {
    this.state.species = species;
    // Cancel any tween — the send-out replaces visual continuity.
    this.state.targetTile = null;
    this.state.tweenStartMs = null;
    this.state.tweenKind = null;
    this.state.hopPending = false;
    // Restart send-out (overwrites any in-flight one).
    this.state.sendOutInProgress = true;
    this.state.visible = true;
  }

  /**
   * Resolve any pending transient state so the caller can transition to
   * dialogue or encounter with a stable home tile. Called from
   * `WorldScene.showDialogue` (before opening) and
   * `WorldScene.triggerEncounter` (before SLEEP).
   *
   *  - hopPending → fast-forward the hop. The follower lands at one
   *    tile behind the player. The caller is responsible for supplying
   *    the appropriate tile via the wrapper; here we just snap-clear
   *    the flag. (The Phaser wrapper would also fast-forward its
   *    sin-arc tween to the final landing position.)
   *  - sendOutInProgress → snap-complete the send-out (sprite reads as
   *    fully emerged; the gate-flag drops so the caller can proceed).
   */
  flushPending(): void {
    if (this.state.hopPending) {
      this.state.hopPending = false;
    }
    if (this.state.sendOutInProgress) {
      this.state.sendOutInProgress = false;
    }
  }

  /** Wrapper signals the send-out animation timer completed. */
  completeSendOut(): void {
    this.state.sendOutInProgress = false;
  }

  // ─── Internal helpers ───────────────────────────────────────────────

  /** Place follower on `preferredTile` when the caller named one and
   *  it's walkable, else one tile behind the player (opposite of
   *  facing). If that tile is blocked, auto-shuffle to nearest walkable
   *  cardinal, then diagonal. If all 8 are blocked, hide + flag
   *  pendingReveal. Triggers send-out on the chosen tile. */
  private placeBehindOrShuffle(
    playerTile: Tile,
    facing: Facing,
    npcTiles: ReadonlySet<string> | undefined,
    preferredTile?: Tile,
  ): void {
    const v = FACING_VECTORS[facing];
    const behind: Tile = {
      x: playerTile.x - v.dx,
      y: playerTile.y - v.dy,
    };

    let placement: Tile | null = null;
    if (preferredTile && !isBlocked(preferredTile.x, preferredTile.y, npcTiles)) {
      placement = preferredTile;
    } else if (!isBlocked(behind.x, behind.y, npcTiles)) {
      placement = behind;
    } else {
      placement = this.findAdjacentWalkable(playerTile, npcTiles);
    }

    // Clear any tween / hop state — send-out is the canonical
    // animation in this beat; previous tweens are stale.
    this.state.targetTile = null;
    this.state.tweenStartMs = null;
    this.state.tweenKind = null;
    this.state.hopPending = false;

    if (placement) {
      this.state.tileX = placement.x;
      this.state.tileY = placement.y;
      this.state.visible = true;
      this.state.pendingReveal = false;
      // Send-out animation gates player movement for ~1s; the wrapper
      // calls completeSendOut() when its timer fires.
      this.state.sendOutInProgress = true;
    } else {
      this.state.tileX = playerTile.x;
      this.state.tileY = playerTile.y;
      this.state.visible = false;
      this.state.pendingReveal = true;
      this.state.sendOutInProgress = false;
    }
  }

  /** Search the 8 adjacent tiles around `origin` in cardinal-then-
   *  diagonal order; return the first walkable, or null if all 8 are
   *  blocked. */
  private findAdjacentWalkable(
    origin: Tile,
    npcTiles: ReadonlySet<string> | undefined,
  ): Tile | null {
    for (const { dx, dy } of CARDINAL_OFFSETS) {
      const x = origin.x + dx;
      const y = origin.y + dy;
      if (!isBlocked(x, y, npcTiles)) return { x, y };
    }
    for (const { dx, dy } of DIAGONAL_OFFSETS) {
      const x = origin.x + dx;
      const y = origin.y + dy;
      if (!isBlocked(x, y, npcTiles)) return { x, y };
    }
    return null;
  }
}
